import { animate, style, transition, trigger } from '@angular/animations';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GlobalAddInputAction,
  GlobalAddOutcomeAction,
} from '@app/global-add/global-add.model';
import { GlobalAddService } from '@app/global-add/services/global-add.service';
import { InputPermissionsService } from '@app/inputs/services/input-permissions.service';
import { UserOutcomeModalService } from '@app/outcomes/services/user-outcome-modal.service';
import { PathwayAddEditFormModalService } from '@app/pathways/services/pathway-add-edit-form-modal-service';
import { PathwayPermissionsService } from '@app/pathways/services/pathway-permissions.service';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { DialogData, DialogService } from '@degreed/apollo';
import {
  ContextService,
  InputType,
  TrackerService,
} from '@degreed/lxp/services';
import { AuthService, AuthUser } from '@degreed/lxp/services/authorization';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dgx-add-content',
  templateUrl: 'add-content.component.html',
  styleUrls: ['./add-content.component.scss'],
  animations: [
    trigger('PanelTrigger', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(95%)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'scale(100%)' })
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(100%)' }),
        animate(
          '200ms ease-in-out',
          style({ opacity: 0, transform: 'scale(95%)' })
        ),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddContentComponent implements OnInit {
  public outcomeActions: Partial<GlobalAddOutcomeAction>[];
  public inputActions: Partial<GlobalAddInputAction>[];
  public i18n = this.translateService.instant([
    'dgUtilityBar_AddToProfileTitle',
    'dgGlobalAdd_Content',
    'dgGlobalAdd_Description',
    'dgGlobalAdd_Achievement',
    'dgGlobalAdd_AchievementDescription',
  ]);
  public authUser: AuthUser;
  public showIntegrationsSection: boolean;
  public extensionsText: string;
  public integrationsText: string;

  private isChannelContext: boolean;

  private readonly pathwayPermissionsService = inject(
    PathwayPermissionsService
  );
  private readonly inputPermissionsService = inject(InputPermissionsService);

  constructor(
    public dialogRef: DialogRef,
    @Inject(DIALOG_DATA) public data: DialogData,
    private authService: AuthService,
    private contextService: ContextService,
    private globalAddService: GlobalAddService,
    private translateService: TranslateService,
    private pathwayAddEditModalService: PathwayAddEditFormModalService,
    private genericInputModalService: InputModalDispatcherService,
    private userOutcomeModalService: UserOutcomeModalService,
    private trackerService: TrackerService,
    private dialogService: DialogService,
    private destroyRef: DestroyRef
  ) {}

  public ngOnInit() {
    this.dialogRef.addPanelClass('apollo-dialog-panel');

    this.dialogRef.backdropClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleClose();
      });

    this.dialogRef.keydownEvents
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          this.handleClose();
        }
      });

    this.isChannelContext = this.contextService.urlHasContext(
      location.href,
      'channel'
    );
    this.authService.authUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authUser) => {
        this.authUser = authUser;
        this.showIntegrationsSection =
          !this.authUser?.orgInfo[0]?.settings
            ?.hidePersonalIntegrationsInUserProfileSettings;
      });

    // PD-130899: under ReBAC enforcement the content affordances (Article,
    // Video, Book, …) require the `addContent` capability. ReBAC OFF keeps the
    // items for everyone (this panel was previously ungated), and consumer users
    // are never permission-checked — see `InputPermissionsService.canCreate`.
    // Outcomes are deliberately NOT gated: they go through
    // `UserOutcomeModalService`, never enter the content catalog, and
    // `addContent` is a catalog permission.
    this.inputActions = this.inputPermissionsService.canCreate()
      ? this.globalAddService.globalAddInputs
          .filter((input) => input.type !== 'Task')
          .map((input) => {
            return {
              ...input,
              ...{
                handlerFn: this.handleInput,
                dgat: 'globalAdd-69e',
              },
            };
          })
      : [];
    // PD-130899: hide the affordance when the user may not create a pathway.
    // `PathwayAddEditFormModalService.openModal` returns EMPTY for a denied
    // user, so leaving the item in place would close this panel and do nothing.
    // ReBAC OFF keeps the item for everyone (legacy behavior).
    if (this.pathwayPermissionsService.canCreate()) {
      this.inputActions.push({
        type: 'CreatePathway',
        handlerFn: this.createPathway,
        dgat: 'globalAdd-843',
      });
    }
    this.outcomeActions = this.globalAddService.globalAddOutcomes.map(
      (outcome) => {
        return {
          ...outcome,
          ...{
            handlerFn: this.handleOutcome,
            dgat: 'globalAdd-21a',
          },
        };
      }
    );

    const settingsUrl = '/me/settings#';

    const hideExtension =
      this.authUser?.defaultOrgInfo?.settings?.restrictExtension;
    this.extensionsText = hideExtension
      ? ''
      : this.translateService.instant('dgGlobalAdd_ExtensionsFormat', {
          startAnchor: `<a href="${settingsUrl}/extensions" class="link" data-dgat="globalAdd-78c">`,
          endAnchor: '</a>',
        });

    this.integrationsText = this.translateService.instant(
      'dgGlobalAdd_IntegrationsFormat',
      {
        startAnchor: `<a href="${settingsUrl}/integrations" class="link" data-dgat="globalAdd-ddd">`,
        endAnchor: '</a>',
      }
    );
  }

  // arrow function for context
  public handleInput = (event: Event, input: GlobalAddInputAction) => {
    this.handleClose();
    this.genericInputModalService
      .complete({
        inputType: input.type as InputType,
        sourceTarget: undefined, // this.menuButtonRef.elementRef.nativeElement,
        trackingArea: 'Global Add',
      })
      .pipe(takeUntilDestroyed(this.destroyRef));
  };

  // arrow function for context
  public handleOutcome = (event: Event, outcome: GlobalAddOutcomeAction) => {
    this.handleClose();
    this.userOutcomeModalService
      .add({
        outcomeType: outcome.type,
        sourceTarget: undefined, // this.menuButtonRef.elementRef.nativeElement,
        trackingAction: 'Achievement Added',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  };

  // arrow function for maintaining context coming from ajs
  public createPathway = () => {
    const options = {
      pathId: 0,
      forCloning: false,
      itemToAdd: null,
      isChannel: this.isChannelContext,
      disableEndorsedOption: true,
      trackingArea: 'Global Add',
    };

    this.handleClose();
    this.pathwayAddEditModalService
      .openModal(options)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  };

  public handleClose(): void {
    this.dialogService.prepareForClose();
    this.dialogRef.close();
  }

  public trackGlobalAddClicked() {
    this.trackerService.trackEventData({
      action: 'Global Add Clicked',
      properties: {},
    });
  }
}
